import type { CompositionEntry } from '@internal-audit/domain';
import type { FlexibleAuditSession } from '@internal-audit/domain/graphql-types';

import { CompositionPanel } from '../../shared/CompositionPanel';
import * as denseStyles from '../InformationDenseLayout.css';

interface LanguageTopicsPanelProps {
  session: FlexibleAuditSession;
}

/**
 * Panel component for displaying language and topic composition data
 * Shows breakdown of dataset composition by language and topic with visual bars
 * Now includes overflow handling with fade-out gradient and tooltip
 */
// eslint-disable-next-line complexity
export function LanguageTopicsPanel({ session }: LanguageTopicsPanelProps) {
  // Use datasetComposition if available, otherwise fallback to sample statistics
  const datasetComposition = session?.datasetComposition;
  const sampleStats = session?.sampleStatistics;

  // Create fallback composition from sample statistics if datasetComposition is not available
  const effectiveComposition =
    datasetComposition ||
    (sampleStats
      ? {
          byLanguage: sampleStats.languageCounts || [],
          byTopic: sampleStats.topicCounts || [],
          byLabel: sampleStats.labelCounts || [],
          bySeverity: [],
          totalItems: sampleStats.sampleSize || 0,
        }
      : null);

  // Combine languages and topics into a single array for overflow handling
  const allEntries: CompositionEntry[] = [];

  if (effectiveComposition) {
    // Add languages with a prefix to distinguish them
    effectiveComposition.byLanguage.forEach((entry: { key: string; count: number; percentage?: number }) => {
      allEntries.push({
        key: `${entry.key} (lang)`,
        count: entry.count,
        percentage: entry.percentage || (effectiveComposition.totalItems > 0 ? Math.round((entry.count / effectiveComposition.totalItems) * 100) : 0),
      });
    });

    // Add topics with a prefix to distinguish them
    effectiveComposition.byTopic.forEach((entry: { key: string; count: number; percentage?: number }) => {
      allEntries.push({
        key: `${entry.key} (topic)`,
        count: entry.count,
        percentage: entry.percentage || (effectiveComposition.totalItems > 0 ? Math.round((entry.count / effectiveComposition.totalItems) * 100) : 0),
      });
    });
  }

  const hasData = allEntries.length > 0;
  const totalCount = effectiveComposition?.totalItems || 0;

  // Split entries into visible and hidden for overflow handling
  const MAX_VISIBLE_ITEMS = 5;
  const visibleEntries = hasData ? allEntries.slice(0, MAX_VISIBLE_ITEMS) : [];
  const hiddenEntries = hasData ? allEntries.slice(MAX_VISIBLE_ITEMS) : [];

  return (
    <div className={denseStyles.detailsPanel}>
      <div className={denseStyles.detailsPanelHeader}>
        <span className={denseStyles.detailsPanelTitle}>Language & Topics</span>
        <span className={denseStyles.compositionCount}>{hasData ? `${allEntries.length} types` : 'No data'}</span>
      </div>

      {hasData ? (
        <CompositionPanel colorType="languages" hiddenItems={hiddenEntries} title="Language & Topics" totalItems={totalCount} visibleItems={visibleEntries} />
      ) : (
        <div className={denseStyles.detailsPanelContent}>
          <div className={denseStyles.noCompositionMessage}>No language or topic composition data available</div>
        </div>
      )}
    </div>
  );
}
